import ServiceIssue, {
  SERVICE_ISSUE_FIELDS,
  deriveVendorCaseOpen,
} from "../../../classes/ServiceIssue.js";
import ServiceWorkOrder from "../../../classes/ServiceWorkOrder.js";
import CalendarEvent from "../../../classes/CalendarEvent.js";
import { db, storage } from "../../../boot/firebase";
import {
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  increment,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../boot/firebase";
import { forEach, isEmpty, pick, without } from "lodash";
import { serviceIssueDataFromIssue } from "../../../util/serviceIssueMigration.js";

// Server-side because a sale is usually locked by the time it is in service,
// and the rules refuse client writes to a locked sale.
const closeOutLegacyPunchout = httpsCallable(
  functions,
  "closeOutLegacyPunchout"
);

// ============================================
// SERVICE ISSUES
// ============================================

export const GET_SERVICE_ISSUE = async (
  { dispatch },
  { issueID, forceUpdate }
) => {
  return dispatch("GET_DOC", {
    converter: ServiceIssue,
    path: ServiceIssue.getPath(issueID),
    stateKey: "serviceIssues",
    id: issueID,
    forceUpdate,
  });
};

export const GET_SERVICE_ISSUES = (
  { dispatch },
  { where, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: ServiceIssue,
    stateKey: "serviceIssues",
    path: ServiceIssue.getPath(),
    where,
    limitCount,
    pagination,
  });
};

export const LISTEN_SERVICE_ISSUE = async (
  { dispatch },
  { issueID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: ServiceIssue,
    path: ServiceIssue.getPath(issueID),
    stateKey: "serviceIssues",
    id: issueID,
    next,
    error,
    unlisten,
  });
};

// Every resolved service issue the company has ever closed is a set that only
// grows, so the views that listen across resolved/all issues take a cap. See
// RESOLVED_ISSUE_LISTENER_LIMIT.
export const LISTEN_SERVICE_ISSUES = (
  { dispatch },
  { where, listenerPath, limitCount, error, next, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: ServiceIssue,
    stateKey: "serviceIssues",
    path: ServiceIssue.getPath(),
    listenerPath,
    where,
    limitCount,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_PROJECT_SERVICE_ISSUES = (
  { dispatch },
  { projectID, listenerPath, error, next, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: ServiceIssue,
    stateKey: "serviceIssues",
    path: ServiceIssue.getPath(),
    listenerPath: listenerPath || `serviceIssues-project-${projectID}`,
    where: ["projectID", "==", projectID],
    next,
    error,
    unlisten,
  });
};

export const SAVE_SERVICE_ISSUE = async ({ dispatch, state }, { issue }) => {
  const batch = writeBatch(db);

  // Upload new documents (photos/video, same "service-issue-media" type) and
  // delete removed ones. No `parentRef` is passed: the `batch.set` below
  // writes the whole issue, so SAVE_DOCUMENTS only needs to mutate the file
  // URLs in place before that happens.
  const saveDocuments = (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      batch,
      parentData: issue.data,
      fieldPath: "media",
      folderPath: issue.path,
      existingData,
    });
  };

  // Stamp the parts clock the first time this issue needs parts. Nothing else
  // wrote partsRequiredDate, so GET_SERVICE_METRICS's "days to order parts"
  // silently fell back to the review/report date.
  const stampPartsRequired = (previous = null) => {
    if (issue.data.partsRequired && !previous?.partsRequired) {
      issue.data.partsRequiredDate = issue.data.partsRequiredDate || new Date();
    }
  };

  // The sale's service rollup (`status.service.*`) is NOT written here.
  // functions/service.js onServiceIssueWrite recounts it from the sale's
  // issues on every write that changes it. A sale in service is usually
  // locked, the rules refuse a client write to a locked sale, and a batch
  // that carried the rollup alongside the issue was rejected whole — a tech
  // reporting a found issue on a locked sale's punchout got
  // permission-denied and no issue at all.

  if (!issue.id) {
    // New Issue
    issue.data.created = new Date();
    issue.data.createdBy = state.user.id;
    issue.data.reportedDate = issue.data.reportedDate || new Date();

    const ref = doc(collection(db, ServiceIssue.getPath()));
    issue.id = ref.id;

    stampPartsRequired(null);
    await saveDocuments();
    batch.set(ref.withConverter(ServiceIssue), issue);
    await batch.commit();

    // TODO: Send notification to service department for new customer-reported issues
    if (issue.data.isCustomerReported) {
      // dispatch("NOTIFY_SERVICE_DEPARTMENT", { issue });
    }
  } else {
    // Existing Issue
    issue.data.modified = new Date();
    issue.data.modifiedBy = state.user.id;

    const current = await dispatch("GET_SERVICE_ISSUE", {
      issueID: issue.id,
    });

    stampPartsRequired(current.data);
    await saveDocuments(current.data);
    batch.set(doc(db, issue.path).withConverter(ServiceIssue), issue);
    await batch.commit();

    // Handle billableTo changes - notifications and commission adjustments
    if (current.data.billableTo !== issue.data.billableTo) {
      await dispatch("HANDLE_BILLABLE_TO_CHANGE", {
        issue,
        previousBillableTo: current.data.billableTo,
        newBillableTo: issue.data.billableTo,
      });
    }
  }
  return issue;
};

// Handle billableTo change notifications and commission adjustments
export const HANDLE_BILLABLE_TO_CHANGE = async (
  { dispatch, state },
  { issue, previousBillableTo, newBillableTo }
) => {
  const projectID = issue.data.projectID;
  const saleID = issue.data.saleID;

  if (!projectID || !saleID) return;

  const sale = state.sales[projectID + saleID];
  const project = state.projects[projectID];

  if (!sale) return;

  // Get estimated cost for this issue (parts + labor estimate)
  const issueCost =
    (issue.data.partsEstimatedCost || 0) +
    (issue.data.estimatedHours || 0) * 75; // Assume $75/hr labor rate

  // Determine notifications and commission changes based on billableTo
  const changes = {
    notifyAccounting: false,
    notifySalesperson: false,
    commissionAdjustment: null, // null = no change, number = adjustment amount
    adjustmentReason: "",
  };

  // Handle based on NEW billableTo value
  switch (newBillableTo) {
    case "vendor":
      // Vendor pays - notify accounting to process vendor claim
      changes.notifyAccounting = true;
      changes.adjustmentReason = `Service issue billed to vendor: ${
        issue.data.description || issue.id
      }`;
      break;

    case "customer":
      // Customer pays - notify salesperson AND create sales lead
      changes.notifySalesperson = true;
      changes.createSalesLead = true;
      changes.adjustmentReason = `Service issue billed to customer: ${
        issue.data.description || issue.id
      }`;
      break;

    case "internal":
      // Job cost - affects commission
      // If changing TO internal, add cost to commission calculation
      changes.commissionAdjustment = issueCost;
      changes.adjustmentReason = `Service job cost added: ${
        issue.data.description || issue.id
      }`;
      break;

    case "nonbillable":
      // Not billable - no commission impact
      break;
  }

  // If parts are required, mark vendor as not fully ordered and notify salesperson
  if (issue.data.partsRequired && !issue.data.partsOrderID) {
    changes.markVendorNotFullyOrdered = true;
    changes.notifySalesperson = true;
  }

  // Handle reversal if changing FROM internal (was affecting commission)
  if (previousBillableTo === "internal" && newBillableTo !== "internal") {
    // Remove the cost that was previously added
    changes.commissionAdjustment = -issueCost;
    changes.adjustmentReason = `Service job cost removed (changed to ${newBillableTo}): ${
      issue.data.description || issue.id
    }`;
  }

  // Send notifications
  if (changes.notifyAccounting) {
    try {
      await dispatch("SEND_SERVICE_BILLING_NOTIFICATION", {
        type: "accounting",
        issue,
        billableTo: newBillableTo,
        project,
        sale,
      });
    } catch (error) {
      console.error("Failed to notify accounting:", error);
    }
  }

  if (changes.notifySalesperson && sale.data.soldBy) {
    try {
      await dispatch("SEND_SERVICE_BILLING_NOTIFICATION", {
        type: "salesperson",
        issue,
        billableTo: newBillableTo,
        project,
        sale,
        salespersonID: sale.data.soldBy,
      });
    } catch (error) {
      console.error("Failed to notify salesperson:", error);
    }
  }

  // Handle commission adjustment
  if (
    changes.commissionAdjustment !== null &&
    changes.commissionAdjustment !== 0
  ) {
    try {
      await dispatch("ADJUST_SERVICE_COMMISSION", {
        projectID,
        saleID,
        issueID: issue.id,
        amount: changes.commissionAdjustment,
        reason: changes.adjustmentReason,
      });
    } catch (error) {
      console.error("Failed to adjust commission:", error);
    }
  }

  // Create sales lead for customer-billable service
  if (changes.createSalesLead) {
    try {
      await dispatch("CREATE_SERVICE_SALES_LEAD", {
        issue,
        project,
        sale,
      });
    } catch (error) {
      console.error("Failed to create sales lead:", error);
    }
  }

  // Mark vendor as not fully ordered if parts required
  if (changes.markVendorNotFullyOrdered) {
    try {
      await dispatch("MARK_VENDOR_NEEDS_ORDER", {
        projectID,
        saleID,
        issue,
      });
    } catch (error) {
      console.error("Failed to mark vendor not fully ordered:", error);
    }
  }

  console.log("BillableTo change handled:", {
    issueID: issue.id,
    from: previousBillableTo,
    to: newBillableTo,
    changes,
  });
};

// Send notification about service billing
export const SEND_SERVICE_BILLING_NOTIFICATION = async (
  { dispatch, state },
  { type, issue, billableTo, project, sale, salespersonID }
) => {
  const projectName =
    project?.data?.name || project?.data?.address?.street || "Project";
  const issueDesc = issue.data.description || "Service issue";

  if (type === "accounting") {
    // Get accounting users (admins or users with accounting role)
    // User.roles maps role KEYS to descriptor objects that have no `.key` (and
    // can be null for unknown roles), so the old `r.key === "accounting"`
    // never matched. hasRole() checks the raw role keys — same test the rest
    // of the store uses (see invoices.js).
    const accountingUsers = Object.values(state.users || {}).filter(
      (u) => u.isAdmin || u.hasRole?.("accounting")
    );

    for (const user of accountingUsers) {
      if (user.data?.emails?.[0]?.address) {
        const subject = `Service Billing: ${
          billableTo === "vendor" ? "Vendor Claim" : "Customer Invoice"
        } - ${projectName}`;
        const message = `
          <p>A service issue requires billing attention:</p>
          <p><strong>Project:</strong> ${projectName}</p>
          <p><strong>Issue:</strong> ${issueDesc}</p>
          <p><strong>Bill To:</strong> ${billableTo}</p>
          <p><strong>Estimated Cost:</strong> $${(
            issue.data.partsEstimatedCost || 0
          ).toFixed(2)}</p>
          <p>Please review and process accordingly.</p>
        `;

        await dispatch("SEND_EMAIL", {
          to: user.data.emails[0].address,
          subject,
          message,
        });
      }
    }
  } else if (type === "salesperson" && salespersonID) {
    const salesperson = state.users?.[salespersonID];
    const contact = salesperson ? state.contacts?.[salespersonID] : null;
    const email = contact?.data?.emails?.[0]?.address;

    if (email) {
      const subject = `Service Issue - Customer Billing Required - ${projectName}`;
      const message = `
        <p>A service issue on your project will be billed to the customer:</p>
        <p><strong>Project:</strong> ${projectName}</p>
        <p><strong>Issue:</strong> ${issueDesc}</p>
        <p><strong>Estimated Cost:</strong> $${(
          issue.data.partsEstimatedCost || 0
        ).toFixed(2)}</p>
        <p>Please communicate with the customer about this charge.</p>
      `;

      await dispatch("SEND_EMAIL", {
        to: email,
        subject,
        message,
      });
    }
  }
};

// Adjust commission for service job costs
export const ADJUST_SERVICE_COMMISSION = async (
  { dispatch, state },
  { projectID, saleID, issueID, amount, reason }
) => {
  const sale = state.sales[projectID + saleID];
  if (!sale) return;

  // Get all salespeople with commission on this sale
  const assignedTo = sale.data.assignedTo || [];

  for (const userID of assignedTo) {
    const commissionID = projectID + saleID;
    const commission = state.commission?.[commissionID + userID];

    if (commission) {
      const isPaid = commission.data.totalPaid > 0;

      // Record the service cost adjustment
      const adjustment = {
        date: new Date(),
        issueID,
        amount,
        reason,
        type: "service_cost",
        appliedBy: state.user.id,
      };

      // If commission already paid, this creates a deduction on next payment
      // If not yet paid, this adds to the cost basis
      const updatePath = `users/${userID}/commission/${commissionID}`;

      await updateDoc(doc(db, updatePath), {
        // Add to actual cost (reduces margin and commission)
        actualCost: increment(amount),
        // Track the adjustment
        serviceAdjustments: arrayUnion(adjustment),
        modified: new Date(),
        modifiedBy: state.user.id,
      });

      console.log(
        `Commission adjusted for ${userID}: ${
          isPaid ? "PAID - will deduct from next" : "NOT PAID - added to cost"
        }`,
        { amount, reason }
      );
    }
  }
};

// Create a sales lead for customer-billable service issue
export const CREATE_SERVICE_SALES_LEAD = async (
  { dispatch },
  { issue, project, sale }
) => {
  if (!project || !sale) return;

  // Build notes from issue details
  const issueType = issue.data.issueType || "Service";
  const description = issue.data.description || "";
  const estimatedCost = issue.data.partsEstimatedCost || 0;

  const notes = `SERVICE ISSUE - CUSTOMER BILLABLE

Issue Type: ${issueType}
Description: ${description}
Estimated Cost: $${estimatedCost.toFixed(2)}
${issue.data.partsRequired ? "Parts Required: Yes" : ""}
${
  issue.data.estimatedHours
    ? `Estimated Hours: ${issue.data.estimatedHours}`
    : ""
}

Original Issue ID: ${issue.id}
Created from service issue on ${new Date().toLocaleDateString()}`;

  // Create sales lead data
  const SalesLead = (await import("../../../classes/SalesLead")).default;
  const leadData = {
    receivedFrom: project.data.soldTo || null,
    receivedDate: new Date(),
    type: project.data.type === "commercial" ? "office" : "remodel",
    address: project.data.address || issue.data.serviceAddress || {},
    notes,
    projectID: project.id,
    // Without this the SAVE_SALES_LEAD back-link hook never fires, so the
    // issue's salesLeadID stays empty and the lead is orphaned.
    serviceIssueID: issue.id,
    // Deliberately OPEN and unassigned. SalesLeadStatus only carries
    // `assignedTo` inside its `qualified` bucket, so assigning here would mean
    // opening the lead as `qualified` — and a service issue that just became
    // customer-billable has not been qualified by anyone yet. It goes into the
    // normal open-lead queue; whoever qualifies it assigns it. The originating
    // salesperson is still recoverable from `sale.data.soldBy` via projectID.
    status: { type: "open" },
  };

  const lead = new SalesLead(null, leadData);
  await dispatch("SAVE_SALES_LEAD", lead);

  console.log("Sales lead created for customer-billable service:", {
    issueID: issue.id,
    leadID: lead.id,
  });

  return lead;
};

// Mark vendor as not fully ordered when service parts are required
export const MARK_VENDOR_NEEDS_ORDER = async (
  { dispatch, state },
  { projectID, saleID, issue }
) => {
  const sale = state.sales[projectID + saleID];
  if (!sale) return;

  // Find the vendor that would supply parts for this issue
  // Default to first vendor if not specified
  const vendors = Object.keys(sale.data.status?.vendors || {});
  if (vendors.length === 0) return;

  // Use the issue's vendor if specified, otherwise first vendor
  const vendorID = issue.data.vendorID || vendors[0];

  // Update the vendor's fullyOrdered status to false
  await dispatch("UPDATE_SALE_STATUS", {
    projectID,
    saleID,
    changes: {
      [`status.vendors.${vendorID}.fullyOrdered`]: false,
      [`status.vendors.${vendorID}.servicePartsNeeded`]: true,
      [`status.vendors.${vendorID}.serviceIssueID`]: issue.id,
    },
  });

  // Notify salesperson about the ordering requirement
  const salespersonID = sale.data.soldBy;
  if (salespersonID) {
    const contact = state.contacts[salespersonID];
    const email = contact?.data?.emails?.[0]?.address;
    const project = state.projects[projectID];
    const projectName =
      project?.data?.name || project?.data?.address?.street || "Project";

    if (email) {
      await dispatch("SEND_EMAIL", {
        to: email,
        subject: `Service Parts Order Required - ${projectName}`,
        message: `
          <p>A service issue requires parts to be ordered:</p>
          <p><strong>Project:</strong> ${projectName}</p>
          <p><strong>Issue:</strong> ${
            issue.data.description || "Service issue"
          }</p>
          <p><strong>Estimated Cost:</strong> $${(
            issue.data.partsEstimatedCost || 0
          ).toFixed(2)}</p>
          <p>Please create an order for the required parts.</p>
        `,
      });
    }
  }

  console.log("Vendor marked as needing order:", {
    projectID,
    saleID,
    vendorID,
    issueID: issue.id,
  });
};

// The fields the stored vendorCaseOpen is derived from (deriveVendorCaseOpen).
const VENDOR_CASE_INPUTS = [
  "partsBilling",
  "closeVendorCase",
  "vendorCaseClosed",
];

export const UPDATE_SERVICE_ISSUE = async (
  { dispatch, state },
  { issueID, changes }
) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  // No caller changes the vendor case's inputs this way today, but nothing
  // stops one, and a partial write would leave the stored flag (and so Vendor
  // Cases to Close) wrong. Restamp it from the issue as it will be.
  if (VENDOR_CASE_INPUTS.some((key) => key in changes)) {
    const issue = await dispatch("GET_SERVICE_ISSUE", {
      issueID,
      forceUpdate: true,
    });
    changes.vendorCaseOpen = deriveVendorCaseOpen({
      ...(issue ? issue.data : {}),
      ...changes,
    });
  }
  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);
};

export const MARK_SERVICE_ISSUE_REVIEWED = async ({ state }, { issueID }) => {
  const changes = {
    isReviewed: true,
    reviewedDate: new Date(),
    modified: new Date(),
    modifiedBy: state.user.id,
  };

  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);
};

export const HOLD_SERVICE_ISSUE = async ({ state }, { issueID, reason }) => {
  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), {
    onHold: true,
    onHoldReason: reason || "",
    onHoldDate: new Date(),
    onHoldBy: state.user.id,
    modified: new Date(),
    modifiedBy: state.user.id,
  });
};

export const RELEASE_SERVICE_ISSUE_HOLD = async ({ state }, { issueID }) => {
  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), {
    onHold: false,
    onHoldReason: "",
    onHoldDate: null,
    onHoldBy: "",
    modified: new Date(),
    modifiedBy: state.user.id,
  });
};

// One write to the issue and nothing else: the sale's rollup is recounted by
// onServiceIssueWrite (see SAVE_SERVICE_ISSUE), so no locked sale can refuse
// a resolution.
export const RESOLVE_SERVICE_ISSUE = async (
  { state },
  { issueID, resolution }
) => {
  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), {
    isResolved: true,
    resolution: resolution || "",
    completedDate: new Date(),
    modified: new Date(),
    modifiedBy: state.user.id,
  });
};

/**
 * Record that the vendor closed the warranty case for an issue, under its
 * case number when one is given: the number is often only known by now, so
 * it can be added or corrected here, and a blank one leaves the number on
 * record alone. Clearing the stored vendorCaseOpen takes the issue off
 * Vendor Cases to Close.
 *
 * The issue is read fresh, not from the store: a case someone already closed
 * on another device keeps who closed it and when, and `closed: false` comes
 * back.
 */
export const CLOSE_SERVICE_VENDOR_CASE = async (
  { dispatch, state },
  { issueID, caseNumber = "" }
) => {
  const issue = await dispatch("GET_SERVICE_ISSUE", {
    issueID,
    forceUpdate: true,
  });
  if (issue && issue.data.vendorCaseClosed) {
    return { closed: false };
  }

  const now = new Date();
  const changes = {
    vendorCaseClosed: true,
    vendorCaseClosedDate: now,
    vendorCaseClosedBy: state.user.id,
    vendorCaseOpen: false,
    modified: now,
    modifiedBy: state.user.id,
  };
  const number = String(caseNumber || "").trim();
  if (number) changes.vendorCaseNumber = number;

  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);
  return { closed: true };
};

export const DELETE_SERVICE_ISSUE = async (
  { dispatch, state },
  { issueID }
) => {
  // Get current issue to check if linked to sale
  const issue =
    state.serviceIssues[issueID] ||
    (await dispatch("GET_SERVICE_ISSUE", { issueID }));

  // Drop the issue's media from Storage first; once the doc is gone their
  // filenames are unrecoverable and the files would be orphaned.
  if (issue?.data.media?.length) {
    await dispatch("SAVE_DOCUMENTS", {
      parentData: { media: [] },
      existingData: issue.data,
      fieldPath: "media",
      folderPath: ServiceIssue.getPath(issueID),
    });
  }

  // The sale's rollup drops the issue through onServiceIssueWrite (see
  // SAVE_SERVICE_ISSUE); nothing here writes the sale.
  await deleteDoc(doc(db, ServiceIssue.getPath(issueID)));
};

// ============================================
// SERVICE WORK ORDERS
// ============================================

export const GET_SERVICE_WORK_ORDER = async ({ dispatch }, { workOrderID }) => {
  return dispatch("GET_DOC", {
    converter: ServiceWorkOrder,
    path: ServiceWorkOrder.getPath(workOrderID),
    stateKey: "serviceWorkOrders",
    id: workOrderID,
  });
};

export const GET_SERVICE_WORK_ORDERS = (
  { dispatch },
  { where, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: ServiceWorkOrder,
    stateKey: "serviceWorkOrders",
    path: ServiceWorkOrder.getPath(),
    where,
    limitCount,
    pagination,
  });
};

export const LISTEN_SERVICE_WORK_ORDER = async (
  { dispatch },
  { workOrderID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: ServiceWorkOrder,
    path: ServiceWorkOrder.getPath(workOrderID),
    stateKey: "serviceWorkOrders",
    id: workOrderID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_SERVICE_WORK_ORDERS = (
  { dispatch },
  { where, listenerPath, error, next, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: ServiceWorkOrder,
    stateKey: "serviceWorkOrders",
    path: ServiceWorkOrder.getPath(),
    listenerPath,
    where,
    next,
    error,
    unlisten,
  });
};

export const SAVE_SERVICE_WORK_ORDER = async (
  { dispatch, state },
  { workOrder }
) => {
  const batch = writeBatch(db);

  const saveDocuments = (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      batch,
      parentData: workOrder.data,
      fieldPath: "attachments",
      folderPath: workOrder.path,
      existingData,
    });
  };

  // Link issues to this work order, and release any that were taken off it.
  //
  // `previousIssueIDs` is the work order's saved issue list. An issue dropped
  // from the list has to have its workOrderID cleared or it stays invisible to
  // every other work order's available-issues picker (which excludes issues
  // claimed by a different work order) and keeps reading as scheduled.
  const linkIssues = async (previousIssueIDs = []) => {
    for (const issueID of workOrder.data.issueIDs) {
      batch.update(doc(db, ServiceIssue.getPath(issueID)), {
        workOrderID: workOrder.id,
        addedToWorkOrderDate: new Date(),
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }

    const removed = without(previousIssueIDs, ...workOrder.data.issueIDs);
    for (const issueID of removed) {
      batch.update(doc(db, ServiceIssue.getPath(issueID)), {
        workOrderID: "",
        addedToWorkOrderDate: null,
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }
  };

  // Create calendar event if work order has scheduling data
  const createCalendarEvent = async () => {
    if (
      workOrder.data.scheduledDate &&
      (workOrder.data.assignedTechs?.length || workOrder.data.vehicles?.length)
    ) {
      // Build start and end dates from scheduledDate + time strings
      const baseDate = new Date(workOrder.data.scheduledDate);
      let start = new Date(baseDate);
      let end = new Date(baseDate);

      // Parse start time (HH:MM format)
      if (workOrder.data.scheduledStartTime) {
        const [startHours, startMinutes] = workOrder.data.scheduledStartTime
          .split(":")
          .map(Number);
        start.setHours(startHours, startMinutes, 0, 0);
      }

      // Parse end time (HH:MM format)
      if (workOrder.data.scheduledEndTime) {
        const [endHours, endMinutes] = workOrder.data.scheduledEndTime
          .split(":")
          .map(Number);
        end.setHours(endHours, endMinutes, 0, 0);
      } else {
        // Default to start time if no end time
        end = new Date(start);
      }

      const participants = workOrder.data.assignedTechs || [];
      const vehicles = workOrder.data.vehicles || [];

      // Generate a unique instance ID
      const instanceId = Math.random().toString(36).slice(-12);

      const eventData = {
        type: "service",
        item: workOrder.id,
        start: start,
        end: end,
        participants: participants,
        vehicles: vehicles,
        title: workOrder.data.serviceAddress?.street || "Service",
        description: workOrder.data.internalNotes || "",
        location: workOrder.data.serviceAddress || null,
        // Instances are required for field events to show on calendar
        instances: {
          [instanceId]: {
            start: start,
            end: end,
            participants: participants,
            vehicles: vehicles,
          },
        },
        created: new Date(),
        createdBy: state.user.id,
      };
      const eventRef = doc(collection(db, CalendarEvent.getPath()));
      const event = new CalendarEvent(eventRef.id, eventData);
      batch.set(eventRef.withConverter(CalendarEvent), event);
      workOrder.data.eventID = eventRef.id;
    }
  };

  // Update existing calendar event if schedule data changed
  const updateCalendarEvent = async () => {
    if (
      workOrder.data.eventID &&
      workOrder.data.scheduledDate &&
      (workOrder.data.assignedTechs?.length || workOrder.data.vehicles?.length)
    ) {
      // Build start and end dates from scheduledDate + time strings
      const baseDate = new Date(workOrder.data.scheduledDate);
      let start = new Date(baseDate);
      let end = new Date(baseDate);

      // Parse start time (HH:MM format)
      if (workOrder.data.scheduledStartTime) {
        const [startHours, startMinutes] = workOrder.data.scheduledStartTime
          .split(":")
          .map(Number);
        start.setHours(startHours, startMinutes, 0, 0);
      }

      // Parse end time (HH:MM format)
      if (workOrder.data.scheduledEndTime) {
        const [endHours, endMinutes] = workOrder.data.scheduledEndTime
          .split(":")
          .map(Number);
        end.setHours(endHours, endMinutes, 0, 0);
      } else {
        end = new Date(start);
      }

      const participants = workOrder.data.assignedTechs || [];
      const vehicles = workOrder.data.vehicles || [];

      // Generate a unique instance ID (prefixed with 'i' to ensure valid field path)
      const instanceId = "i" + Math.random().toString(36).slice(-11);

      const eventRef = doc(db, CalendarEvent.getPath(workOrder.data.eventID));

      // Delete old instances first (must include modifiedBy for Firestore rules)
      await updateDoc(eventRef, {
        instances: deleteField(),
        modified: new Date(),
        modifiedBy: state.user.id,
      });

      // Now update with new data including new instances in the batch
      batch.update(eventRef, {
        start: start,
        end: end,
        participants: participants,
        vehicles: vehicles,
        title: workOrder.data.serviceAddress?.street || "Service",
        description: workOrder.data.internalNotes || "",
        location: workOrder.data.serviceAddress || null,
        instances: {
          [instanceId]: {
            start: start,
            end: end,
            participants: participants,
            vehicles: vehicles,
          },
        },
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }
  };

  if (!workOrder.id) {
    // New Work Order
    workOrder.data.created = new Date();
    workOrder.data.createdBy = state.user.id;

    const ref = doc(collection(db, ServiceWorkOrder.getPath()));
    workOrder.id = ref.id;

    await saveDocuments();
    await linkIssues();
    await createCalendarEvent();
    batch.set(ref.withConverter(ServiceWorkOrder), workOrder);
    await batch.commit();
  } else {
    // Existing Work Order
    workOrder.data.modified = new Date();
    workOrder.data.modifiedBy = state.user.id;

    const current = await dispatch("GET_SERVICE_WORK_ORDER", {
      workOrderID: workOrder.id,
    });

    await saveDocuments(current.data);
    await linkIssues(current.data.issueIDs);

    // Update calendar event if schedule has changed
    if (workOrder.data.eventID) {
      await updateCalendarEvent();
    } else if (
      workOrder.data.scheduledDate &&
      (workOrder.data.assignedTechs?.length || workOrder.data.vehicles?.length)
    ) {
      // Create new calendar event if none exists but we have schedule data
      await createCalendarEvent();
    }

    batch.set(
      doc(db, workOrder.path).withConverter(ServiceWorkOrder),
      workOrder
    );
    await batch.commit();
  }
  return workOrder;
};

export const UPDATE_SERVICE_WORK_ORDER = async (
  { dispatch, state },
  { workOrderID, changes }
) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  await updateDoc(doc(db, ServiceWorkOrder.getPath(workOrderID)), changes);
};

export const COMPLETE_SERVICE_WORK_ORDER = async (
  { dispatch, state },
  {
    workOrderID,
    signature,
    signerName,
    // Two callers, two shapes for the same "customer did not sign" fact: the
    // tech page sends the boolean, the office dialog's "absent" tab sends a
    // reason string. Accept both — requiring only `noCustomerOnSite` made the
    // office dialog's absent path throw every time.
    noCustomerOnSite,
    noSignatureReason,
    completionNotes,
    completionPhotos,
    resolvedIssueIDs,
    deferredIssueIDs,
    inspectedIssueIDs,
  }
) => {
  const batch = writeBatch(db);

  // Validate signoff: a signature, an explicit "no customer on site", or a
  // written reason for the missing signature.
  if (
    !signature &&
    !noCustomerOnSite &&
    !String(noSignatureReason || "").trim()
  ) {
    throw new Error("Customer signature required when customer is on site");
  }

  // Get work order to check for chargeable items
  const workOrder = await dispatch("GET_SERVICE_WORK_ORDER", { workOrderID });
  const hasChargeableItems = workOrder.issues?.some(
    (issue) => issue.data.serviceType === "chargeable"
  );

  // Upload completion photos to storage and get URLs
  const uploadedPhotos = [];
  if (completionPhotos && completionPhotos.length) {
    const folderPath = `${ServiceWorkOrder.getPath(
      workOrderID
    )}/completion-photos`;
    for (const photo of completionPhotos) {
      // If photo already has a URL (already uploaded), keep it
      if (photo.file?.url) {
        uploadedPhotos.push({
          type: photo.type,
          file: {
            name: photo.file.name,
            url: photo.file.url,
            type: photo.file.type,
          },
        });
      } else if (photo.file?.toSave) {
        // Upload new file to storage
        const ref = storageRef(storage, `${folderPath}/${photo.file.name}`);
        await uploadBytes(ref, photo.file.toSave);
        const url = await getDownloadURL(ref);
        uploadedPhotos.push({
          type: photo.type,
          file: {
            name: photo.file.name,
            url: url,
            type: photo.file.type,
          },
        });
      }
    }
  }

  // Calculate actual hours from startedDate to now
  const completedDate = new Date();
  let actualHours = 0;
  let actualManHours = 0;
  if (workOrder.data.startedDate) {
    const startTime =
      workOrder.data.startedDate instanceof Date
        ? workOrder.data.startedDate
        : new Date(workOrder.data.startedDate);
    actualHours =
      Math.round(((completedDate - startTime) / (1000 * 60 * 60)) * 100) / 100;
    const techCount = workOrder.data.assignedTechs?.length || 1;
    actualManHours = Math.round(actualHours * techCount * 100) / 100;
  }

  // Update work order
  const workOrderChanges = {
    status: "completed",
    completedDate,
    completedBy: state.user.id,
    actualHours,
    actualManHours,
    signature: signature || null,
    signerName: signerName || "",
    noCustomerOnSite: noCustomerOnSite || false,
    noSignatureReason: noSignatureReason || "",
    completionNotes: completionNotes || "",
    completionPhotos: uploadedPhotos,
    completedIssueIDs: resolvedIssueIDs || [],
    followUpNeeded: (deferredIssueIDs?.length || 0) > 0,
    modified: new Date(),
    modifiedBy: state.user.id,
  };
  batch.update(
    doc(db, ServiceWorkOrder.getPath(workOrderID)),
    workOrderChanges
  );

  // Resolve linked issues
  if (resolvedIssueIDs && resolvedIssueIDs.length) {
    for (const issueID of resolvedIssueIDs) {
      batch.update(doc(db, ServiceIssue.getPath(issueID)), {
        isResolved: true,
        completedDate: new Date(),
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }
  }

  // Mark deferred issues (needs parts, quote, or another visit)
  // Clear workOrderID so the issue re-enters the service center pipeline
  // Reset isReviewed so it appears in "Issues Under Review" for manager triage
  if (deferredIssueIDs && deferredIssueIDs.length) {
    for (const issueID of deferredIssueIDs) {
      batch.update(doc(db, ServiceIssue.getPath(issueID)), {
        deferredDate: new Date(),
        workOrderID: "",
        isReviewed: false,
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }
  }

  // Mark inspected issues: the inspection confirmed what the repair needs.
  // Record inspection completion and release the work order link, but keep
  // isReviewed so the issue advances to its next pipeline status
  // (needs_quote / awaiting_parts_order / needs_scheduled) instead of
  // bouncing back to "Issues Under Review".
  if (inspectedIssueIDs && inspectedIssueIDs.length) {
    for (const issueID of inspectedIssueIDs) {
      batch.update(doc(db, ServiceIssue.getPath(issueID)), {
        inspectionCompleted: true,
        inspectionCompletedDate: new Date(),
        workOrderID: "",
        addedToWorkOrderDate: null,
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }
  }

  await batch.commit();

  // Generate invoice for chargeable items
  if (hasChargeableItems) {
    try {
      await dispatch("CREATE_SERVICE_INVOICE", {
        workOrderID,
        status: "pending_review", // Sent to accounting for review
      });
    } catch (error) {
      console.error("Failed to create service invoice:", error);
      // Don't fail the completion, just log the error
    }
  }

  // Send completion notification to customer
  try {
    await dispatch("SEND_SERVICE_COMPLETION_NOTIFICATION", {
      workOrderID,
      resolvedIssueIDs,
      deferredIssueIDs,
      hasUnresolvedIssues:
        (deferredIssueIDs?.length || 0) > 0 ||
        workOrder.issues?.length > (resolvedIssueIDs?.length || 0),
    });
  } catch (error) {
    console.error("Failed to send completion notification:", error);
    // Don't fail the completion, just log the error
  }
};

export const CANCEL_SERVICE_WORK_ORDER = async (
  { dispatch, state },
  { workOrderID }
) => {
  const batch = writeBatch(db);

  // Get the work order to find the calendar event
  const workOrder = await dispatch("GET_SERVICE_WORK_ORDER", { workOrderID });
  if (!workOrder) {
    throw new Error("Work order not found");
  }

  // Delete the calendar event if it exists
  if (workOrder.data.eventID) {
    const eventRef = doc(db, CalendarEvent.getPath(workOrder.data.eventID));
    batch.delete(eventRef);
  }

  // Unlink issues from this work order
  if (workOrder.data.issueIDs?.length) {
    for (const issueID of workOrder.data.issueIDs) {
      batch.update(doc(db, ServiceIssue.getPath(issueID)), {
        workOrderID: "",
        addedToWorkOrderDate: null,
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }
  }

  // Update the work order status
  batch.update(doc(db, ServiceWorkOrder.getPath(workOrderID)), {
    status: "cancelled",
    scheduledDate: null,
    scheduledStartTime: null,
    scheduledEndTime: null,
    assignedTechs: [],
    eventID: "",
    modified: new Date(),
    modifiedBy: state.user.id,
  });

  await batch.commit();
};

/**
 * Mark a legacy sale's punchout complete without a service visit.
 *
 * Justin (2026-09-15): sales finished before the Service Center existed were
 * never punched out in the app, and the service manager can confirm against
 * the old service schedule which ones are actually done. Those must not go
 * through scheduling — a phantom visit on the calendar, a customer
 * notification — so the callable closes the sale out directly: it flips
 * status.punchOutComplete, records any screens/hardware still counted as
 * undelivered as delivered (they went in at that punchout, or the sale would
 * still owe one), and quietly completes any pending punchout work order the
 * backfill opened for it. Refused for anything sold since the launch.
 */
export const CLOSE_OUT_LEGACY_PUNCHOUT = async (
  context,
  { projectID, saleID }
) => {
  const result = await closeOutLegacyPunchout({ projectID, saleID });
  return result.data;
};

// Create invoices for chargeable service work
// Groups issues by billableTo to create separate invoices per billing party
export const CREATE_SERVICE_INVOICE = async (
  { dispatch, state },
  { workOrderID, status = "pending_review" }
) => {
  const workOrder = await dispatch("GET_SERVICE_WORK_ORDER", { workOrderID });
  if (!workOrder) {
    throw new Error("Work order not found");
  }

  // Get all billable issues (not just chargeable serviceType)
  const billableIssues =
    workOrder.issues?.filter(
      (issue) =>
        issue.data.billableTo &&
        issue.data.billableTo !== "nonbillable" &&
        issue.data.billableTo !== "internal"
    ) || [];

  if (billableIssues.length === 0) {
    return null; // No billable items
  }

  // Group issues by billableTo (vendor, customer, etc.)
  const issuesByBillTo = {};
  billableIssues.forEach((issue) => {
    const billTo = issue.data.billableTo;
    if (!issuesByBillTo[billTo]) {
      issuesByBillTo[billTo] = [];
    }
    issuesByBillTo[billTo].push(issue);
  });

  // Create separate invoice data for each billing party
  const invoices = [];

  for (const [billTo, issues] of Object.entries(issuesByBillTo)) {
    const lineItems = [];

    // Add service call fee (split proportionally if multiple bill-to parties)
    const billToCount = Object.keys(issuesByBillTo).length;
    lineItems.push({
      description:
        billToCount > 1
          ? `Service Call (${issues.length}/${billableIssues.length} issues)`
          : "Service Call",
      quantity: 1,
      unitPrice: 0, // TODO: Get from settings
      amount: 0,
    });

    // Add labor proportionally based on estimated hours for these issues
    const totalEstimatedHours = billableIssues.reduce(
      (sum, i) => sum + (i.data.estimatedHours || 0),
      0
    );
    const theseEstimatedHours = issues.reduce(
      (sum, i) => sum + (i.data.estimatedHours || 0),
      0
    );

    if (workOrder.data.actualHours > 0 && totalEstimatedHours > 0) {
      const laborProportion = theseEstimatedHours / totalEstimatedHours;
      const laborHours = workOrder.data.actualHours * laborProportion;
      if (laborHours > 0) {
        lineItems.push({
          description: `Labor (${laborHours.toFixed(1)}h)`,
          quantity: laborHours,
          unitPrice: 0, // TODO: Get labor rate from settings
          amount: 0,
        });
      }
    }

    // Add parts from issues
    issues.forEach((issue) => {
      if (issue.data.partsRequired) {
        lineItems.push({
          description: `Parts: ${issue.data.description || "Service parts"}`,
          quantity: 1,
          unitPrice: issue.data.partsEstimatedCost || 0,
          amount: issue.data.partsEstimatedCost || 0,
          issueID: issue.id,
        });
      }
    });

    // Create invoice data for this billing party
    const invoiceData = {
      type: "service",
      workOrderID,
      projectID: workOrder.data.projectID,
      saleID: workOrder.data.saleID,
      billTo, // vendor, customer
      issueIDs: issues.map((i) => i.id),
      lineItems,
      subtotal: lineItems.reduce((sum, item) => sum + item.amount, 0),
      tax: 0, // TODO: Calculate tax
      total: lineItems.reduce((sum, item) => sum + item.amount, 0),
      status, // pending_review, sent, paid
      isCashSale: workOrder.data.isCashSale || false,
      created: new Date(),
      createdBy: state.user.id,
    };

    invoices.push(invoiceData);

    // TODO: Save invoice to database
    // const invoiceRef = await dispatch("SAVE_INVOICE", { invoice: invoiceData });

    console.log(
      `Service invoice created for ${billTo} (pending implementation):`,
      invoiceData
    );
  }

  // Log warning if issues have conflicting bill-to for same order
  const orderIssueGroups = {};
  billableIssues.forEach((issue) => {
    if (issue.data.partsOrderID) {
      if (!orderIssueGroups[issue.data.partsOrderID]) {
        orderIssueGroups[issue.data.partsOrderID] = new Set();
      }
      orderIssueGroups[issue.data.partsOrderID].add(issue.data.billableTo);
    }
  });

  for (const [orderID, billToSet] of Object.entries(orderIssueGroups)) {
    if (billToSet.size > 1) {
      console.warn(
        `Order ${orderID} has issues with different billTo values:`,
        Array.from(billToSet),
        "- Review may be needed to split order costs appropriately"
      );
    }
  }

  return invoices;
};

// Send completion notification to customer
export const SEND_SERVICE_COMPLETION_NOTIFICATION = async (
  { dispatch, state },
  { workOrderID, resolvedIssueIDs, hasUnresolvedIssues }
) => {
  const workOrder = await dispatch("GET_SERVICE_WORK_ORDER", { workOrderID });
  if (!workOrder) return;

  // Get project to find customer contact
  const project = workOrder.data.projectID
    ? state.projects[workOrder.data.projectID]
    : null;
  if (!project) {
    console.warn("No project found for service completion notification");
    return;
  }

  // Get customer contact from project's soldTo
  const customerContactID = project.data.soldTo?.contact;
  const customerContact = customerContactID
    ? state.contacts[customerContactID]
    : null;

  // Find customer email
  const customerEmail = customerContact?.data?.emails?.[0]?.address;
  if (!customerEmail) {
    console.warn("No customer email found for service completion notification");
    return;
  }

  // Get resolved issues for the email
  const resolvedIssues = (resolvedIssueIDs || [])
    .map((id) => state.serviceIssues[id])
    .filter(Boolean);

  // Build email subject
  const subject = `Service Completed - ${
    project.data.name || project.data.address?.street || "Your Project"
  }`;

  // Build email message
  let message = `
    <p>Dear ${customerContact?.data?.name || "Valued Customer"},</p>
    <p>We are pleased to inform you that our service visit has been completed.</p>
  `;

  // Add service address
  const serviceAddress = workOrder.data.serviceAddress || project.data.address;
  if (serviceAddress) {
    const addressStr = [
      serviceAddress.street,
      serviceAddress.city,
      serviceAddress.state,
      serviceAddress.zip,
    ]
      .filter(Boolean)
      .join(", ");
    message += `<p><strong>Service Location:</strong> ${addressStr}</p>`;
  }

  // Add completion date
  if (workOrder.data.completedDate) {
    const completedDate =
      workOrder.data.completedDate instanceof Date
        ? workOrder.data.completedDate.toLocaleDateString()
        : new Date(workOrder.data.completedDate).toLocaleDateString();
    message += `<p><strong>Completed:</strong> ${completedDate}</p>`;
  }

  // Add resolved issues summary
  if (resolvedIssues.length > 0) {
    message += `<p><strong>Issues Resolved (${resolvedIssues.length}):</strong></p><ul>`;
    for (const issue of resolvedIssues) {
      const issueType = issue.data.issueType || "Service";
      const description = issue.data.description || "Issue resolved";
      message += `<li><strong>${issueType}:</strong> ${description}</li>`;
    }
    message += `</ul>`;
  }

  // Add note about unresolved issues
  if (hasUnresolvedIssues) {
    message += `
      <p><em>Note: Some items still require attention. Our team will follow up with you shortly to schedule additional service.</em></p>
    `;
  }

  // Add completion notes if provided
  if (workOrder.data.completionNotes) {
    message += `<p><strong>Technician Notes:</strong> ${workOrder.data.completionNotes}</p>`;
  }

  // Add signature info
  if (workOrder.data.signerName) {
    message += `<p><strong>Signed by:</strong> ${workOrder.data.signerName}</p>`;
  } else if (workOrder.data.noCustomerOnSite) {
    message += `<p><em>Customer was not present at time of service.</em></p>`;
  }

  // Closing
  message += `
    <p>Thank you for choosing our services. If you have any questions or concerns about the work performed, please don't hesitate to contact us.</p>
    <p>Best regards,<br/>The Service Team</p>
  `;

  // Send the email
  try {
    await dispatch("SEND_EMAIL", {
      to: customerEmail,
      subject,
      message,
    });
    console.log("Service completion notification sent to:", customerEmail);
  } catch (error) {
    console.error("Failed to send service completion email:", error);
    throw error;
  }
};

export const DELETE_SERVICE_WORK_ORDER = async (
  { dispatch, state },
  { workOrderID }
) => {
  const batch = writeBatch(db);

  // Get work order to unlink issues
  const workOrder = await dispatch("GET_SERVICE_WORK_ORDER", { workOrderID });

  // Unlink issues
  if (workOrder && workOrder.data.issueIDs) {
    for (const issueID of workOrder.data.issueIDs) {
      batch.update(doc(db, ServiceIssue.getPath(issueID)), {
        workOrderID: "",
        addedToWorkOrderDate: null,
        modified: new Date(),
        modifiedBy: state.user.id,
      });
    }
  }

  batch.delete(doc(db, ServiceWorkOrder.getPath(workOrderID)));
  return batch.commit();
};

// ============================================
// SERVICE METRICS
// ============================================

export const GET_SERVICE_METRICS = async ({ state }) => {
  const issues = Object.values(state.serviceIssues);
  const openIssues = issues.filter((i) => !i.data.isResolved);
  const resolvedIssues = issues.filter((i) => i.data.isResolved);

  // Helper to calculate days between two dates
  const daysBetween = (start, end) => {
    if (!start || !end) return null;
    const startDate = start instanceof Date ? start : new Date(start);
    const endDate = end instanceof Date ? end : new Date(end);
    return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  // ============================================
  // OVERDUE ISSUES
  // - ServiceIssue.isOverdue: open 45+ days, excluding punch list items.
  //   Urgency is a separate axis with its own filter, so an urgent issue
  //   reported today is not overdue.
  // ============================================
  const overdueIssues = openIssues.filter((issue) => issue.data.isOverdue);

  // ============================================
  // URGENT ISSUES
  // - ServiceIssue.isUrgent: urgency 3, the "Urgent Only" filter's rule
  // ============================================
  const urgentIssues = openIssues.filter((issue) => issue.isUrgent);

  // ============================================
  // OPEN ISSUES
  // - Any unresolved issue regardless of status
  // - Includes QC issues, loading issues, delivery issues, etc.
  // ============================================
  // openIssues already captures all unresolved issues

  // ============================================
  // PARTS PENDING
  // - ServiceIssue.isPartsPending: parts required and not yet received
  // ============================================
  const partsPendingIssues = openIssues.filter((i) => i.isPartsPending);

  // ============================================
  // AVERAGE METRICS CALCULATIONS
  // ============================================

  // Average Days to Complete (reported to resolved)
  let totalDaysToComplete = 0;
  let countComplete = 0;
  resolvedIssues.forEach((issue) => {
    const days = daysBetween(issue.data.reportedDate, issue.data.completedDate);
    if (days !== null && days >= 0) {
      totalDaysToComplete += days;
      countComplete++;
    }
  });
  const avgDaysToComplete =
    countComplete > 0 ? Math.round(totalDaysToComplete / countComplete) : 0;

  // Average Days to Review (reported to reviewed)
  let totalDaysToReview = 0;
  let countReviewed = 0;
  issues.forEach((issue) => {
    if (issue.data.isReviewed && issue.data.reportedDate) {
      // Use reviewedDate if available, otherwise use modified date as fallback
      const reviewedDate = issue.data.reviewedDate || issue.data.modified;
      const days = daysBetween(issue.data.reportedDate, reviewedDate);
      if (days !== null && days >= 0) {
        totalDaysToReview += days;
        countReviewed++;
      }
    }
  });
  const avgDaysToReview =
    countReviewed > 0 ? Math.round(totalDaysToReview / countReviewed) : 0;

  // Average Days to Order Parts (from marking needs parts to ordering)
  let totalDaysToOrder = 0;
  let countOrdered = 0;
  issues.forEach((issue) => {
    if (issue.data.partsOrdered && issue.data.partsOrderedDate) {
      // Use partsRequiredDate if available, otherwise use reviewedDate or reportedDate
      const needsPartsDate =
        issue.data.partsRequiredDate ||
        issue.data.reviewedDate ||
        issue.data.reportedDate;
      const days = daysBetween(needsPartsDate, issue.data.partsOrderedDate);
      if (days !== null && days >= 0) {
        totalDaysToOrder += days;
        countOrdered++;
      }
    }
  });
  const avgDaysToOrderParts =
    countOrdered > 0 ? Math.round(totalDaysToOrder / countOrdered) : 0;

  // Average Days from Parts Received to Resolved
  let totalDaysPartsToResolved = 0;
  let countPartsResolved = 0;
  resolvedIssues.forEach((issue) => {
    if (issue.data.partsReceived && issue.data.partsReceivedDate) {
      const days = daysBetween(
        issue.data.partsReceivedDate,
        issue.data.completedDate
      );
      if (days !== null && days >= 0) {
        totalDaysPartsToResolved += days;
        countPartsResolved++;
      }
    }
  });
  const avgDaysPartsToResolved =
    countPartsResolved > 0
      ? Math.round(totalDaysPartsToResolved / countPartsResolved)
      : 0;

  return {
    // Counts
    totalOpen: openIssues.length,
    totalResolved: resolvedIssues.length,
    urgentCount: urgentIssues.length,
    overdueCount: overdueIssues.length,
    partsPendingCount: partsPendingIssues.length,

    // Average metrics
    avgDaysToComplete,
    avgDaysToReview,
    avgDaysToOrderParts,
    avgDaysPartsToResolved,

    // Sample sizes for context
    sampleSizeComplete: countComplete,
    sampleSizeReviewed: countReviewed,
    sampleSizeOrdered: countOrdered,
    sampleSizePartsResolved: countPartsResolved,

    // Issue lists (for drilling down)
    urgentIssues,
    overdueIssues,
    partsPendingIssues,
  };
};

// ============================================
// PARTS ORDERING & APPROVAL
// ============================================

/**
 * Request parts for a service issue
 * - For warranty: auto-approve and allow ordering
 * - For chargeable: set approval pending and notify customer
 */
export const REQUEST_SERVICE_PARTS = async (
  { dispatch, state },
  { issueID, estimatedCost, partsDescription }
) => {
  const issue =
    state.serviceIssues[issueID] ||
    (await dispatch("GET_SERVICE_ISSUE", { issueID }));

  if (!issue) {
    throw new Error("Service issue not found");
  }

  const isChargeable = issue.data.serviceType === "chargeable";

  const changes = {
    partsRequired: true,
    // First time parts are asked for — this is the clock GET_SERVICE_METRICS
    // measures "days to order parts" from.
    partsRequiredDate: issue.data.partsRequiredDate || new Date(),
    partsEstimatedCost: estimatedCost || 0,
    partsNotes: partsDescription || issue.data.partsNotes,
    partsApprovalRequired: isChargeable,
    partsApprovalStatus: isChargeable ? "pending" : "approved",
    partsApprovalDate: isChargeable ? null : new Date(),
    partsApprovalBy: isChargeable ? "" : state.user.id,
    modified: new Date(),
    modifiedBy: state.user.id,
  };

  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);

  // TODO: If chargeable, send approval request notification to customer

  return { isChargeable, approvalRequired: isChargeable };
};

/**
 * Approve or reject parts for a chargeable service issue
 */
export const APPROVE_SERVICE_PARTS = async (
  { dispatch, state },
  { issueID, approved, rejectionReason }
) => {
  const changes = {
    partsApprovalStatus: approved ? "approved" : "rejected",
    partsApprovalDate: new Date(),
    partsApprovalBy: state.user.id,
    modified: new Date(),
    modifiedBy: state.user.id,
  };

  if (!approved && rejectionReason) {
    changes.partsNotes =
      (await dispatch("GET_SERVICE_ISSUE", { issueID })).data.partsNotes +
      `\n[REJECTED: ${rejectionReason}]`;
  }

  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);

  return { approved };
};

/**
 * Link a service issue to the order its parts were ordered under -- a sale
 * order, like the reorders purchasers place for a repair (SAVE_ACK calls this
 * for an ack filed for an issue). Receiving that order then receives the
 * issue's parts. The ack's vendor order number and expected date, when given,
 * go on the issue too; an issue already ordered keeps its ordered date.
 */
export const LINK_SERVICE_ISSUE_TO_ORDER = async (
  { dispatch, state },
  { issueID, orderID, projectID, saleID, orderNumber, expectedDate }
) => {
  const issue =
    state.serviceIssues[issueID] ||
    (await dispatch("GET_SERVICE_ISSUE", { issueID }));

  if (!issue) {
    throw new Error("Service issue not found");
  }

  // Verify approval if chargeable
  if (
    issue.data.partsApprovalRequired &&
    issue.data.partsApprovalStatus !== "approved"
  ) {
    throw new Error("Parts must be approved before ordering");
  }

  const now = new Date();
  const changes = {
    partsOrderID: orderID,
    partsOrdered: true,
    partsOrderedDate:
      (issue.data.partsOrdered && issue.data.partsOrderedDate) || now,
    // Update project/sale link if not already set
    projectID: issue.data.projectID || projectID,
    saleID: issue.data.saleID || saleID,
    modified: now,
    modifiedBy: state.user.id,
  };
  if (orderNumber) changes.partsOrderNumber = String(orderNumber).trim();
  if (expectedDate) changes.partsExpectedDate = expectedDate;

  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);

  return { orderID };
};

/**
 * Take back what an acknowledgement recorded on a service issue, when the ack
 * moves off the issue or is deleted: a sale ack's order
 * (LINK_SERVICE_ISSUE_TO_ORDER, `orderID`), or one not for a sale, which
 * records only the number (ORDER_SERVICE_PARTS, no `orderID`; the number
 * may be its `orderPO`, standing in for the vendor's). The issue
 * drops the order, and the number and date that came with it, so receiving
 * no longer offers its parts; a number from somewhere else (Order Parts)
 * stays. It stays ordered: nothing says whether it was ordered before the
 * ack, most waiting issues already were, and one put back to "Awaiting parts
 * order" by mistake invites a second order. An issue linked to another
 * order, or whose parts already arrived, is left be, and one deleted since
 * has nothing left to take back.
 */
export const UNLINK_SERVICE_ISSUE_FROM_ORDER = async (
  { dispatch, state },
  { issueID, orderID = "", orderNumber = "", orderPO = "" }
) => {
  let issue = state.serviceIssues[issueID];
  if (!issue) {
    try {
      issue = await dispatch("GET_SERVICE_ISSUE", { issueID });
    } catch (error) {
      if (error === "Not found") return;
      throw error;
    }
  }
  if (
    !issue ||
    issue.data.partsReceived ||
    (issue.data.partsOrderID || "") !== orderID
  ) {
    return;
  }

  const number = String(issue.data.partsOrderNumber || "").trim();
  const ours =
    !number ||
    [orderNumber, orderPO].some((n) => number === String(n || "").trim());
  if (!orderID && !ours) return;

  const now = new Date();
  const changes = { modified: now, modifiedBy: state.user.id };
  if (orderID) changes.partsOrderID = "";
  if (ours) {
    changes.partsOrderNumber = "";
    changes.partsExpectedDate = null;
  }
  try {
    await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);
  } catch (error) {
    // Deleted since the store loaded it.
    if (error && error.code === "not-found") return;
    throw error;
  }
};

/**
 * Record that a service issue's parts were ordered, under the vendor's order
 * number (or service request) when there is one. Parts orders carry no PO of
 * ours, so this number is what receiving recognises the parts by. An
 * acknowledgement filed for the parts passes its expected date too, and our
 * PO (`orderPO`) for when it has no number of the vendor's: that stands in
 * only on an issue with no number, or one the ack recorded itself before
 * (`replaces`), so it never replaces the vendor's number from Order Parts,
 * and the ack never blanks a number. An issue already ordered keeps the date
 * it was ordered.
 */
export const ORDER_SERVICE_PARTS = async (
  { dispatch, state },
  { issueID, orderNumber, orderPO = null, replaces = [], expectedDate = null }
) => {
  const issue =
    state.serviceIssues[issueID] ||
    (await dispatch("GET_SERVICE_ISSUE", { issueID }));
  const now = new Date();
  const changes = {
    partsOrdered: true,
    partsOrderedDate:
      (issue && issue.data.partsOrdered && issue.data.partsOrderedDate) || now,
    modified: now,
    modifiedBy: state.user.id,
  };
  const number = String(orderNumber || "").trim();
  const current = String((issue && issue.data.partsOrderNumber) || "").trim();
  // Order Parts (no `orderPO`) records the number as typed, none included.
  if (number || orderPO === null) {
    changes.partsOrderNumber = number;
  } else if (
    orderPO &&
    (!current || replaces.some((n) => String(n || "").trim() === current))
  ) {
    changes.partsOrderNumber = String(orderPO).trim();
  }
  if (expectedDate) changes.partsExpectedDate = expectedDate;
  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);
};

/**
 * Mark parts as received for a service issue. Parts that arrived were
 * ordered, so an issue nobody marked ordered is marked ordered too; otherwise
 * it would sit at "Awaiting parts order" with its parts on the shelf.
 * `receipt` records where receiving took them in (sessionID, truckNumber,
 * orderPO, vendorOrderNumber).
 *
 * The issue is read fresh, not from the store: a listener that stopped (a
 * dock tablet between trucks) leaves its copy behind, still "waiting on
 * parts" after the issue was resolved or got its parts on another device.
 * Such an issue is left alone and `received: false` comes back.
 */
export const RECEIVE_SERVICE_PARTS = async (
  { dispatch, state },
  { issueID, warehouseLocation, receipt = null }
) => {
  const issue = await dispatch("GET_SERVICE_ISSUE", {
    issueID,
    forceUpdate: true,
  });
  if (
    issue &&
    (issue.data.isResolved ||
      issue.data.partsReceived ||
      !issue.data.partsRequired)
  ) {
    return { received: false, warehouseLocation };
  }

  const now = new Date();
  const changes = {
    partsReceived: true,
    partsReceivedDate: now,
    partsWarehouseLocation: warehouseLocation || "",
    modified: now,
    modifiedBy: state.user.id,
  };
  if (!issue || !issue.data.partsOrdered) {
    changes.partsOrdered = true;
    changes.partsOrderedDate = (issue && issue.data.partsOrderedDate) || now;
  }
  if (receipt) {
    changes.partsReceipt = {
      ...SERVICE_ISSUE_FIELDS.partsReceipt,
      ...receipt,
      receivedBy: state.user.id,
    };
  }

  await updateDoc(doc(db, ServiceIssue.getPath(issueID)), changes);

  // Check if this was the last pending parts for a work order
  if (issue && issue.data.workOrderID) {
    // Check if all issues in work order now have parts received. This one's
    // copy in the store predates the write above, so it counts as received.
    const workOrder = await dispatch("GET_SERVICE_WORK_ORDER", {
      workOrderID: issue.data.workOrderID,
    });

    if (workOrder) {
      const allPartsReceived = workOrder.data.issueIDs.every((id) => {
        const i = state.serviceIssues[id];
        return (
          id === issueID || !i || !i.data.partsRequired || i.data.partsReceived
        );
      });

      if (allPartsReceived) {
        await dispatch("UPDATE_SERVICE_WORK_ORDER", {
          workOrderID: issue.data.workOrderID,
          changes: { partsReady: true },
        });
      }
    }
  }

  return { received: true, warehouseLocation };
};

/**
 * Get available parts/orders for a project address
 * Used to suggest parts when creating service issues
 */
export const GET_AVAILABLE_PARTS_FOR_ADDRESS = async (
  { dispatch, state },
  { projectID }
) => {
  const project =
    state.projects[projectID] || (await dispatch("GET_PROJECT", projectID));

  if (!project) {
    return [];
  }

  // Get all orders for this project's sales
  const orders = [];
  for (const saleID of Object.keys(project.data.sales)) {
    const saleOrders = Object.values(state.orders).filter(
      (o) => o.projectID === projectID && o.saleID === saleID
    );
    orders.push(...saleOrders);
  }

  // Return orders with their items for selection
  return orders.map((order) => ({
    orderID: order.id,
    projectID: order.projectID,
    saleID: order.saleID,
    vendor: order.data.vendor,
    date: order.data.date,
    description: order.data.description,
    po: order.po,
  }));
};

// ============================================
// PROJECT SERVICE PHASE TRANSITIONS
// ============================================

/**
 * Transition a project to a new service phase
 * Valid transitions:
 *   none -> active (when first service issue is created)
 *   active -> punchout_scheduled (when punchout is scheduled)
 *   punchout_scheduled -> punchout_complete (when punchout is completed)
 *   punchout_complete -> completed (when all issues are resolved)
 *   any -> none (when service is cancelled/reset)
 */
export const TRANSITION_TO_SERVICE_PHASE = async (
  { dispatch, state },
  { projectID, newPhase, force = false }
) => {
  const ProjectModule = await import("../../../classes/Project");
  const Project = ProjectModule.default;
  const { SERVICE_PHASES } = ProjectModule;

  // Validate phase
  if (!SERVICE_PHASES[newPhase]) {
    throw new Error(`Invalid service phase: ${newPhase}`);
  }

  // Get current project
  const project =
    state.projects[projectID] || (await dispatch("GET_PROJECT", projectID));

  if (!project) {
    throw new Error("Project not found");
  }

  const currentPhase = project.data.servicePhase || "none";

  // Check valid transitions (unless forced)
  if (!force) {
    const validTransitions = {
      none: ["active"],
      active: ["punchout_scheduled", "none"],
      punchout_scheduled: ["punchout_complete", "active", "none"],
      punchout_complete: ["completed", "active", "none"],
      completed: ["active", "none"],
    };

    const allowed = validTransitions[currentPhase];
    if (!allowed || !allowed.includes(newPhase)) {
      console.error(
        `Service phase cannot change from ${currentPhase} to ${newPhase}`
      );
      throw new Error("servicePhaseChangeNotAllowed");
    }
  }

  // Update project
  await updateDoc(doc(db, Project.getPath(projectID)), {
    servicePhase: newPhase,
    modified: new Date(),
    modifiedBy: state.user.id,
  });

  return newPhase;
};

/**
 * Complete a punchout for a project
 * - Marks all punch list issues as resolved
 * - Transitions project to punchout_complete phase
 * - Optionally creates completion notification
 */
export const COMPLETE_PUNCHOUT = async (
  { dispatch, state },
  {
    projectID,
    workOrderID,
    signature,
    signerName,
    completionNotes,
    noSignatureReason,
    completionPhotos = [],
  }
) => {
  const batch = writeBatch(db);
  const Project = (await import("../../../classes/Project")).default;

  // Get all open punch list issues for this project
  const punchListIssues = Object.values(state.serviceIssues).filter(
    (issue) =>
      issue.data.projectID === projectID &&
      issue.data.urgency === 1 && // Punch list urgency
      !issue.data.isResolved
  );

  // Resolve all punch list issues
  for (const issue of punchListIssues) {
    batch.update(doc(db, ServiceIssue.getPath(issue.id)), {
      isResolved: true,
      completedDate: new Date(),
      resolution: "Completed at punchout",
      modified: new Date(),
      modifiedBy: state.user.id,
    });
  }

  // Update work order if provided
  if (workOrderID) {
    const workOrderChanges = {
      status: "completed",
      completedDate: new Date(),
      completedBy: state.user.id,
      signature: signature || null,
      signerName: signerName || "",
      noSignatureReason: noSignatureReason || "",
      completionNotes: completionNotes || "",
      completionPhotos: completionPhotos,
      modified: new Date(),
      modifiedBy: state.user.id,
    };
    batch.update(
      doc(db, ServiceWorkOrder.getPath(workOrderID)),
      workOrderChanges
    );
  }

  // Transition project to punchout_complete
  batch.update(doc(db, Project.getPath(projectID)), {
    servicePhase: "punchout_complete",
    modified: new Date(),
    modifiedBy: state.user.id,
  });

  await batch.commit();

  // Check if all issues are resolved to potentially complete service entirely
  const remainingIssues = Object.values(state.serviceIssues).filter(
    (issue) => issue.data.projectID === projectID && !issue.data.isResolved
  );

  if (remainingIssues.length === 0) {
    // All issues resolved, transition to completed
    await dispatch("TRANSITION_TO_SERVICE_PHASE", {
      projectID,
      newPhase: "completed",
    });
  }

  return {
    resolvedCount: punchListIssues.length,
    remainingCount: remainingIssues.length,
    phase: remainingIssues.length === 0 ? "completed" : "punchout_complete",
  };
};

// ============================================
// ISSUE MIGRATION
// ============================================

/**
 * Convert an existing Issue (from QC/receiving) to a ServiceIssue
 * This is used when a sale enters the service phase and has unresolved issues
 */
export const CONVERT_ISSUE_TO_SERVICE_ISSUE = async (
  { dispatch, state },
  { projectID, saleID, issueID, serviceType = "warranty" }
) => {
  // Get the original issue
  const Issue = (await import("../../../classes/Issue")).default;
  const originalIssue =
    state.issues[issueID] ||
    (await dispatch("GET_ISSUE", { projectID, saleID, issueID }));

  if (!originalIssue) {
    throw new Error("Issue not found");
  }

  // The mapping is shared with scripts/backfill-service-center.mjs, which
  // converts the Issues that were open when the Service Center shipped.
  const serviceIssueData = serviceIssueDataFromIssue({
    issueID,
    projectID,
    saleID,
    issue: originalIssue.data,
    serviceType,
  });

  // Create the new ServiceIssue
  const serviceIssue = new ServiceIssue(null, serviceIssueData);
  const savedIssue = await dispatch("SAVE_SERVICE_ISSUE", {
    issue: serviceIssue,
  });

  // Optionally mark original issue as migrated (add a flag or resolve it)
  await dispatch("UPDATE_ISSUE", {
    projectID,
    saleID,
    issueID,
    changes: {
      migratedToServiceIssue: savedIssue.id,
    },
  });

  return savedIssue;
};

/**
 * Get all unresolved Issues for a sale that could be converted to ServiceIssues
 */
export const GET_CONVERTIBLE_ISSUES = async (
  { dispatch, state },
  { projectID, saleID }
) => {
  const sale = state.sales[projectID + saleID];
  if (!sale || !sale.data.status.issues) {
    return [];
  }

  const issueIDs = Object.keys(sale.data.status.issues);
  const issues = [];

  for (const issueID of issueIDs) {
    const issue =
      state.issues[issueID] ||
      (await dispatch("GET_ISSUE", { projectID, saleID, issueID }));

    // Only include unresolved issues that haven't been migrated
    if (issue && !issue.data.isResolved && !issue.data.migratedToServiceIssue) {
      issues.push(issue);
    }
  }

  return issues;
};
